<script setup lang="ts">
import type { RequireAtLeastOne } from 'type-fest';
import type { Item as KMultipleAutocompleteFieldItem } from '@/components/fields/KMultipleAutocompleteField.vue';
import type { DateRangePickerFieldValue } from '@/components/pickerFields/DateRangePickerField.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { nextTick, reactive, ref, toRaw, watch } from 'vue';
import { format } from 'date-fns';

import KBtn from '@/components/btns/KBtn.vue';
import DriverIdMultipleAutocompleteField from '@/components/fields/DriverIdMultipleAutocompleteField.vue';
import KFetchMultipleAutocompleteTextField from '@/components/fields/KFetchMultipleAutocompleteTextField.vue';
import KMultipleAutocompleteField from '@/components/fields/KMultipleAutocompleteField.vue';
import KSelectField from '@/components/fields/KSelectField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import StateMultiSelectField from '@/components/fields/StateMultiSelectField.vue';
import DateRangePickerField from '@/components/pickerFields/DateRangePickerField.vue';
import useCurrentDateString from '@/composables/useCurrentDateString';
import useForm from '@/composables/useForm';
import { requiredValidationRule } from '@/helpers/validationRules';

export interface InitialData {
  dates?: DateRangePickerFieldValue | null;
  filters?: string[] | null;
  states?: string[] | null;
  customerCodes?: string[] | null;
  serviceCodes?: string[] | null;
  driverIds?: number[] | string[] | null;
  serialNumber?: string | null;
  stopAddress?: string | null;
  combinedData?: string | null;
  payOnHold?: boolean;
}

// for submit try to avoid null (https://github.com/sindresorhus/meta/discussions/7) and use RequireAtLeastOne
// TODO its needed to clear values from nulls and empty arrays before submit
export interface _ValidData {
  dates: DateRangePickerFieldValue;
  filters?: string[];
  states?: string[];
  customerCodes?: string[];
  serviceCodes?: string[];
  driverIds?: number[] | string[];
  serialNumber?: string;
  stopAddress?: string;
  combinedData?: string;
  payOnHold?: boolean;
}

export type ValidData = RequireAtLeastOne<
  _ValidData,
  | 'states'
  | 'customerCodes'
  | 'serviceCodes'
  | 'driverIds'
  | 'serialNumber'
  | 'stopAddress'
  | 'combinedData'
>;

export interface Props {
  loading?: boolean;
  initialData?: InitialData;
  filtersItems?: readonly KMultipleAutocompleteFieldItem[];
  fetchGetLookupCustomer?: (
    params: openapi.LookupCustomerListQueryOpenapi,
  ) => Promise<string[]>;
  fetchGetLookupService?: (
    params: openapi.LookupServiceListOpenapi,
  ) => Promise<string[]>;
  fetchGetLookupDriverByJob?: (
    params: openapi.LookupDriverByJobOpenapi,
  ) => Promise<string[]>;
  isStaff?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  initialData: () => ({}),
  filtersItems: undefined,
  fetchGetLookupCustomer: async () => {
    return await Promise.resolve([]);
  },
  fetchGetLookupService: async () => {
    return await Promise.resolve([]);
  },
  fetchGetLookupDriverByJob: async () => {
    return await Promise.resolve([]);
  },
  isStaff: false,
});
const emit = defineEmits<{
  submit: [formData: ValidData];
  openMultiSearchClick: [event: Event];
}>();

const formData = reactive<InitialData>({});
const isValid = ref(true);

const { form, handleSubmit } = useForm(props, formData, () => {
  emit('submit', toRaw(formData) as ValidData);
});

watch(formData, async (_formData) => {
  if (form.value === null) return;
  // TODO debounce
  await form.value.validate();
});

async function customerCodeFetchItems(customerCode: string): Promise<string[]> {
  return await props.fetchGetLookupCustomer({
    customer_code: customerCode,
    dates: formData.dates?.map((date) => format(date, 'yyyy-MM-dd')).join(','),
    service_code: formData.serviceCodes?.join(','),
    driver_id: formData.driverIds?.join(','),
  });
}

async function serviceCodeFetchItems(serviceCode: string): Promise<string[]> {
  return await props.fetchGetLookupService({
    service_code: serviceCode,
    dates: formData.dates?.map((date) => format(date, 'yyyy-MM-dd')).join(','),
    customer_code: formData.customerCodes?.join(','),
    driver_id: formData.driverIds?.join(','),
  });
}

async function driverIdFetchItems(driverId: string): Promise<string[]> {
  return await props.fetchGetLookupDriverByJob({
    driver_id: driverId,
    dates: formData.dates?.map((date) => format(date, 'yyyy-MM-dd')).join(','),
    customer_code: formData.customerCodes?.join(','),
    service_code: formData.serviceCodes?.join(','),
  });
}

function customRequiredValidationRule(): boolean | string {
  if (
    !formData.states?.length &&
    !formData.customerCodes?.length &&
    !formData.serviceCodes?.length &&
    !formData.driverIds?.length &&
    !formData.serialNumber?.length &&
    !formData.stopAddress?.length &&
    !formData.combinedData?.length &&
    !(formData.payOnHold != null)
  ) {
    return 'At least one is required';
    // return requiredValidationRule(value);
  }
  return true;
}

function handleOpenMultiSearchClick(event: Event): void {
  emit('openMultiSearchClick', event);
}

const { getLast6MthsNext6MthsRange } = useCurrentDateString();

function handleResetClick(): void {
  if (form.value === null) return;
  form.value.reset();
  // TODO same variables in useRouteQuery()
  formData.dates = null; // HACK formData.dates doesn't reset well, need to fix date picker
  void nextTick(() => {
    formData.dates = getLast6MthsNext6MthsRange();
  });
  formData.filters = ['ACTIVE']; // TODO Connect type with filtersItems
}
</script>

<template>
  <v-form
    ref="form"
    v-model="isValid"
    validate-on="lazy"
    :class="$style.advancedSearchSearchForm"
    data-testid="advancedSearchSearchForm"
    @submit.prevent="handleSubmit"
  >
    <DateRangePickerField
      v-model="formData.dates"
      size="lg"
      label="Date range"
      :disabled="props.loading"
      :rules="[requiredValidationRule]"
      :class="$style.field"
    />
    <KMultipleAutocompleteField
      v-model="formData.filters"
      size="lg"
      label="Filters"
      :disabled="props.loading"
      :class="$style.field"
      :items="props.filtersItems"
      @keydown.enter="handleSubmit"
    />
    <StateMultiSelectField
      v-model="formData.states"
      size="large"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="$style.field"
      @keydown.enter="handleSubmit"
    />
    <KFetchMultipleAutocompleteTextField
      v-if="props.isStaff"
      v-model="formData.customerCodes"
      size="lg"
      label="Customer code"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="$style.field"
      :fetch-items="customerCodeFetchItems"
      @keydown.enter="handleSubmit"
    />
    <KFetchMultipleAutocompleteTextField
      v-model="formData.serviceCodes"
      size="lg"
      label="Service code"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="$style.field"
      :fetch-items="serviceCodeFetchItems"
      @keydown.enter="handleSubmit"
    />
    <DriverIdMultipleAutocompleteField
      v-model="formData.driverIds"
      size="lg"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="$style.field"
      :fetch-items="driverIdFetchItems"
      @keydown.enter="handleSubmit"
    />
    <KTextField
      v-model="formData.serialNumber"
      size="lg"
      label="Serial number"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="$style.field"
    />
    <KTextField
      v-model="formData.stopAddress"
      size="lg"
      label="Stop address"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="$style.field"
      messages="Name, line 1 and 2, suburb"
    />
    <KTextField
      v-model="formData.combinedData"
      size="lg"
      label="Combined data"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="[$style.field, $style.combinedDataField]"
      messages="Ref 1-3, product description, job messages, stop notes"
    />

    <KSelectField
      v-model="formData.payOnHold"
      size="lg"
      label="Pay on Hold"
      :items="[
        { title: 'Yes', value: true },
        { title: 'No', value: false },
      ]"
      :disabled="props.loading"
      :rules="[customRequiredValidationRule]"
      :class="$style.field"
      clearable
      @keydown.enter="handleSubmit"
    />
    <br />
    <KBtn
      type="submit"
      size="lg"
      prepend-icon="mdi-magnify"
      :loading="props.loading"
      :class="$style.submit"
    >
      Search
    </KBtn>
    <KBtn
      size="lg"
      variant="outlined"
      :disabled="props.loading"
      :class="$style.multiSearch"
      @click="handleOpenMultiSearchClick"
    >
      Open multi-search
    </KBtn>
    <KBtn
      size="lg"
      variant="text"
      :disabled="props.loading"
      :class="$style.multiSearch"
      @click="handleResetClick"
    >
      Reset all criteria
    </KBtn>
  </v-form>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.advancedSearchSearchForm {
}
  .field {
    width: 17.5rem;
    display: inline-block;
    vertical-align: top;

    &.combinedDataField {
      width: calc((17.5rem * 2) + 1.5rem);
    }
  }
  .field {
    margin-right: 1.5rem;
    margin-bottom: 0.5rem;
  }
  .submit {
    margin-bottom: calc(0.5rem + 22px);
    margin-right: 1.5rem;
  }
  .multiSearch {
    margin-bottom: calc(0.5rem + 22px);
    margin-right: 1.5rem;
  }
</style>
